import TeeBadge from '@/components/ui/tee-badge';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeCheckbox } from '@/components/ui/tee-checkbox';
import {
  buildWeekPosts,
  howToList,
  POST_TEMPLATES,
  postStatus,
  postTaskId,
  type PostStatus,
  type SocialPost,
} from '../currentWeek.social';
import type { GuideContent } from './GuideDrawer';

const statusBadge: Record<PostStatus, 'green' | 'amber' | 'grey'> = {
  posted: 'green',
  today: 'amber',
  late: 'amber',
  catchup: 'amber',
  upcoming: 'grey',
};

interface SocialTabProps {
  week: number;
  goal: number;
  currentWeek: number;
  todayDayOfPlan: number;
  isUpcoming: boolean;
  completedTaskIds: string[];
  onToggle: (taskId: string, completed: boolean) => void;
  onOpenGuide: (content: GuideContent) => void;
}

function guideForPost(post: SocialPost): GuideContent {
  const tpl = post.templateKey ? POST_TEMPLATES[post.templateKey] : undefined;
  const howTos = howToList(0, post.format, post.idea);
  return {
    eyebrow: tpl ? 'Template' : 'Post idea',
    title: tpl?.name ?? 'Post idea',
    intro: post.idea,
    template: tpl,
    howTos,
    playbookAnchor: 'social',
  };
}

export function SocialTab({
  week,
  goal,
  currentWeek,
  todayDayOfPlan,
  isUpcoming,
  completedTaskIds,
  onToggle,
  onOpenGuide,
}: SocialTabProps) {
  const posts = buildWeekPosts(week, goal);
  const done = new Set(completedTaskIds);

  if (posts.length === 0) {
    return (
      <p className="text-body-s text-[var(--text-content-sub-title)]">
        No scheduled posts for this week.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {posts.map((post) => {
        const id = postTaskId(week, post.idx);
        const isDone = done.has(id);
        const { status, label } = postStatus(post, {
          week,
          currentWeek,
          todayDayOfPlan,
          isUpcoming,
          done: isDone,
        });
        const howTos = howToList(week, post.format, post.idea);
        return (
          <div
            key={post.idx}
            className="rounded-[8px] border border-[var(--border-default)] bg-[var(--background-card)] p-3.5 flex items-start gap-3 shadow-[0_1px_2px_rgba(12,61,45,0.05)]"
          >
            <div className="pt-0.5">
              <TeeCheckbox
                checked={isDone}
                onChange={(c) => onToggle(id, c)}
                size="m"
              />
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <TeeBadge label={post.format} type="grey-outline" size="s" />
                <span className="text-[12px] text-[var(--text-content-sub-title)]">
                  {post.day}
                </span>
                <TeeBadge label={label} type={statusBadge[status]} size="s" />
              </div>
              <p
                className={
                  'text-body-s ' +
                  (isDone
                    ? 'text-[var(--text-content-sub-title)] line-through'
                    : 'text-[var(--text-content-title)]')
                }
              >
                {post.idea}
              </p>
              {(post.templateKey || howTos.length > 0) && (
                <div>
                  <TeeButton
                    variant="ghost"
                    size="s"
                    text={
                      post.templateKey && POST_TEMPLATES[post.templateKey]
                        ? 'View template →'
                        : 'How to post →'
                    }
                    onClick={() =>
                      onOpenGuide({ ...guideForPost(post), howTos })
                    }
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
